import React, { useState } from 'react';
import { 
    Box, 
    Typography, 
    Tabs, 
    Tab, 
    Paper, 
    TextField, 
    Button, 
    Alert,
    Container,
    useMediaQuery
} from '@mui/material';
import BookingList from '../components/BookingList';
import BookingForm from '../components/BookingForm';
import AvailabilityCalendar from '../components/AvailabilityCalendar';
import ReportsPage from './ReportsPage';
import BulkBookingsPage from './BulkBookingsPage';
 import PricingRulesPage from './PricingRulesPage';
import DuesPage from './DuesPage';
import InventoryPage from './InventoryPage';
import { adminAPI } from '../services/api';
import { toast } from 'react-toastify';

 const logoColors = {
        primaryBlue: '#3f51b5', // Main blue from logo
        brightYellow: '#c4d835', // Bright yellow/lime from logo
        darkBlue: '#2c387e', // Darker blue for depth
        lightYellow: '#d4e157', // Lighter yellow for highlights
    };

// Login Component
const LoginPage = ({ onLogin }) => {
    const [credentials, setCredentials] = useState({
        username: '',
        password: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCredentials(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const response = await adminAPI.login(credentials);
            
            if (response.data.success) {
                // Store admin token and info
                localStorage.setItem('adminToken', response.data.token);
                localStorage.setItem('adminFullName', response.data.fullName);
                localStorage.setItem('adminUsername', response.data.username);
                localStorage.setItem('isAdminAuthenticated', 'true');
                
                toast.success('Login successful!');
                onLogin();
            } else {
                setError(response.data.message || 'Login failed');
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Login failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="sm">
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        p: 4,
                        width: '100%',
                        maxWidth: 400,
                        background: `linear-gradient(135deg, ${logoColors.darkBlue} 0%, ${logoColors.primaryBlue} 100%)`,
                        color: logoColors.brightYellow,
                        borderRadius: 2,
                        boxShadow: `0 4px 20px ${logoColors.darkBlue}40`
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h1"
                        gutterBottom
                        color={logoColors.brightYellow}
                        sx={{ textAlign: 'center', mb: 3 }}
                    >
                        Admin Login
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <TextField
                            fullWidth
                            label="Username"
                            name="username"
                            value={credentials.username}
                            onChange={handleInputChange}
                            margin="normal"
                            required
                            autoComplete="username"
                            disabled={loading}
                            sx={{ mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: logoColors.brightYellow,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: `${logoColors.brightYellow} !important`
                                    }
                                },
                                '& .MuiInputLabel-root': {
                                    color: logoColors.brightYellow,
                                },
                                '& .MuiInputBase-input': {
                                    color: logoColors.brightYellow,
                                }
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Password"
                            name="password"
                            type="password"
                            value={credentials.password}
                            onChange={handleInputChange}
                            margin="normal"
                            required
                            autoComplete="current-password"
                            disabled={loading}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: logoColors.brightYellow,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: `${logoColors.brightYellow} !important`
                                    }
                                },
                                '& .MuiInputLabel-root': {
                                    color: logoColors.brightYellow,
                                },
                                '& .MuiInputBase-input': {
                                    color: logoColors.brightYellow,
                                }
                            }}
                        />

                         <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            sx={{ 
                                mt: 3, 
                                mb: 2,
                                py: 1.5,
                                fontSize: '1.1rem',
                                borderRadius: 2,
                                backgroundColor: logoColors.brightYellow,
                                color: logoColors.darkBlue,
                                fontWeight: 'bold',
                                '&:hover': {
                                    backgroundColor: logoColors.darkBlue,
                                    color: logoColors.brightYellow,
                                },
                                '&:disabled': {
                                    backgroundColor: 'rgba(196, 216, 53, 0.5)',
                                    color: 'rgba(44, 56, 126, 0.7)',
                                }
                            }}
                            disabled={loading || !credentials.username || !credentials.password}
                        >
                            {loading ? 'Logging in...' : 'Login'}
                        </Button>
                    </form>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                        
                    </Typography>
                </Paper>
            </Box>
        </Container>
    );
};

// Admin Dashboard Component
const AdminDashboard = ({ onLogout }) => {
    const [tabValue, setTabValue] = useState(0);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Phones: scrollable tabs (7 tabs don't fit); larger screens keep the centered layout
    const isPhone = useMediaQuery('(max-width:599.95px)');

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleBookingCreated = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" component="h1" color={logoColors.brightYellow} sx={{ textAlign: 'center', flex: 1 }}>
                    Admin Dashboard
                </Typography>
                <Button 
                    variant="outlined" 
                    color={logoColors.brightYellow}
                    onClick={onLogout}
                    sx={{ ml: 2, borderColor: logoColors.brightYellow , color: logoColors.brightYellow, '&:hover': { borderColor: logoColors.darkBlue, color: logoColors.darkBlue } }}
                >
                    Logout
                </Button>
            </Box>
                     
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    centered={!isPhone}
                    variant={isPhone ? 'scrollable' : 'standard'}
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    textColor="inherit"
                    TabIndicatorProps={{ style: { backgroundColor: logoColors.brightYellow  } }}
                    sx={{
                        color: logoColors.brightYellow,
                        '& .MuiTab-root': { color: logoColors.brightYellow },
                        '& .Mui-selected': { color: logoColors.brightYellow }
                    }}
                >
                    <Tab label="All Bookings" />
                    <Tab label="New Booking" />
                    <Tab label="Reports" />
                    <Tab label="Pricing Rules" />
                    <Tab label="Bulk Bookings" />
                    <Tab label="Dues" />
                    <Tab label="Inventory" />
                    {/* <Tab label="Availability" /> */}
                </Tabs>
            </Box>

            {tabValue === 0 && <BookingList refreshTrigger={refreshTrigger} />}
            {tabValue === 1 && <BookingForm onBookingCreated={handleBookingCreated} isAdmin={true} />}
            {tabValue === 2 && <ReportsPage />}
            {tabValue === 3 && <PricingRulesPage />}
            {tabValue === 4 && <BulkBookingsPage />}
            {tabValue === 5 && <DuesPage />}
            {tabValue === 6 && <InventoryPage />}
            {/* {tabValue === 2 && <AvailabilityCalendar />} */}
        </Box>
    );
};

// Main Admin Page Component
const AdminPage = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const handleLogin = () => {
        setIsAuthenticated(true);
        // You might want to store authentication state in localStorage or context
         localStorage.setItem('isAdminAuthenticated', 'true');
    };

    const handleLogout = () => {
        setIsAuthenticated(false);
        // Clear authentication state
        localStorage.removeItem('isAdminAuthenticated');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminFullName');
        localStorage.removeItem('adminUsername');
        toast.success('Logged out successfully!');
        
        // Stay on admin page - will show login form
        // No redirect needed as component will re-render
    };

    // Check if user was previously authenticated (optional)
    React.useEffect(() => {
        const validateExistingAuth = async () => {
            const token = localStorage.getItem('adminToken');
            const savedAuthState = localStorage.getItem('isAdminAuthenticated');
            
            if (savedAuthState === 'true' && token) {
                try {
                    const response = await adminAPI.validateToken(token);
                    if (response.data === true) {
                        setIsAuthenticated(true);
                    } else {
                        // Token is invalid, clear storage
                        localStorage.removeItem('isAdminAuthenticated');
                        localStorage.removeItem('adminToken');
                        localStorage.removeItem('adminFullName');
                        localStorage.removeItem('adminUsername');
                    }
                } catch (error) {
                    console.error('Token validation failed:', error);
                    // Token validation failed, clear storage
                    localStorage.removeItem('isAdminAuthenticated');
                    localStorage.removeItem('adminToken');
                    localStorage.removeItem('adminFullName');
                    localStorage.removeItem('adminUsername');
                }
            }
            setIsLoading(false);
        };

        validateExistingAuth();
    }, []);

    // Handle navigation-based logout and session management
    React.useEffect(() => {
        // Set a flag that this page is currently active
        sessionStorage.setItem('adminPageActive', 'true');
        
        // Clear any stale auth state (older than 24 hours)
        const clearStaleAuth = () => {
            const token = localStorage.getItem('adminToken');
            if (token) {
                try {
                    const parts = token.split(':');
                    if (parts.length === 2) {
                        const timestamp = parseInt(parts[1]);
                        const now = Date.now();
                        const twentyFourHours = 24 * 60 * 60 * 1000;
                        
                        if (now - timestamp > twentyFourHours) {
                            // Token is older than 24 hours, clear it
                            localStorage.removeItem('isAdminAuthenticated');
                            localStorage.removeItem('adminToken');
                            localStorage.removeItem('adminFullName');
                            localStorage.removeItem('adminUsername');
                        }
                    }
                } catch (error) {
                    // Invalid token format, clear everything
                    localStorage.removeItem('isAdminAuthenticated');
                    localStorage.removeItem('adminToken');
                    localStorage.removeItem('adminFullName');
                    localStorage.removeItem('adminUsername');
                }
            }
        };

        clearStaleAuth();

        // Cleanup function: runs when component unmounts (navigation away)
        return () => {
            // Remove the active flag
            sessionStorage.removeItem('adminPageActive');
            
            // Clear auth when navigating away from admin page
            localStorage.removeItem('isAdminAuthenticated');
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminFullName');
            localStorage.removeItem('adminUsername');
        };
    }, []);

    // Handle page refresh vs navigation detection
    React.useEffect(() => {
        const handleBeforeUnload = () => {
            // This runs on page refresh/close - we want to keep auth for refresh
            // Set a temporary flag that will be cleared after a short delay
            sessionStorage.setItem('adminPageRefresh', 'true');
            
            // Clear the refresh flag after a short delay
            setTimeout(() => {
                sessionStorage.removeItem('adminPageRefresh');
            }, 1000);
        };

        // Check if we're coming back from a refresh
        const wasRefresh = sessionStorage.getItem('adminPageRefresh') === 'true';
        const wasActive = sessionStorage.getItem('adminPageActive') === 'true';
        
        if (!wasRefresh && !wasActive) {
            // This is a fresh navigation to admin (not a refresh)
            // Clear any existing auth to force fresh login
            localStorage.removeItem('isAdminAuthenticated');
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminFullName');
            localStorage.removeItem('adminUsername');
        }

        // Clean up the refresh flag
        sessionStorage.removeItem('adminPageRefresh');

        window.addEventListener('beforeunload', handleBeforeUnload);
        
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    // Show loading while checking authentication
    if (isLoading) {
        return (
            <Container maxWidth="sm">
                <Box
                    sx={{
                        minHeight: '100vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Typography variant="h6" color={logoColors.brightYellow}>
                        Loading...
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <>
            {!isAuthenticated ? (
                <LoginPage onLogin={handleLogin} />
            ) : (
                <AdminDashboard onLogout={handleLogout} />
            )}
        </>
    );
};

export default AdminPage;